import React from "react";
import {PlaidEmbeddedLink, PlaidLinkOnExit, PlaidLinkOnSuccess} from "react-plaid-link";
import {CardDetails} from "@bounce/shared/enums";
import {Box, Button, Container, LoadingCenter, Text} from "@bounce/shared/ui";
import AchMethodPicker from "./AchMethodPicker";

const EMBEDDED_MIN_HEIGHT = 490;

const SectionLoader = () => (
  <Container fullWidth sx={{minHeight: EMBEDDED_MIN_HEIGHT, position: "relative"}}>
    <LoadingCenter position="absolute" />
  </Container>
);

interface PlaidBankSectionProps {
  achMethods: CardDetails[];
  embeddedToken: string | null;
  hasMethods: boolean;
  selectedCardId: string | number | undefined;
  showEmbed: boolean;
  tokenError: boolean;
  walletLoaded: boolean;
  onAddPaymentMethod: () => void;
  onCancelConnect: () => void;
  onRetryConnect: () => void;
  onExit: PlaidLinkOnExit;
  onSuccess: PlaidLinkOnSuccess;
  setSelectedCardId: (id: string | number | undefined) => void;
}

const PlaidBankSection = ({
  achMethods,
  embeddedToken,
  hasMethods,
  selectedCardId,
  showEmbed,
  tokenError,
  walletLoaded,
  onAddPaymentMethod,
  onCancelConnect,
  onRetryConnect,
  onExit,
  onSuccess,
  setSelectedCardId,
}: PlaidBankSectionProps) => {
  if (!walletLoaded) {
    return <SectionLoader />;
  }

  if (!showEmbed) {
    return (
      <AchMethodPicker
        methods={achMethods}
        selectedCardId={selectedCardId}
        onAddPaymentMethod={onAddPaymentMethod}
        setSelectedCardId={setSelectedCardId}
      />
    );
  }

  if (tokenError) {
    return (
      <Container fullWidth sx={{alignItems: "start", gap: "8px"}}>
        <Text variant="inter400">Instant bank login isn't available right now.</Text>
        <Button buttonType="text" type="button" onClick={onRetryConnect}>
          Try again
        </Button>
      </Container>
    );
  }

  if (!embeddedToken) {
    return <SectionLoader />;
  }

  return (
    <Container fullWidth sx={{alignItems: "stretch", gap: "8px"}}>
      <Box
        sx={{
          minHeight: EMBEDDED_MIN_HEIGHT,
          width: {xs: "calc(100% + 40px)", sm: "100%"},
          mx: {xs: "-20px", sm: 0},
        }}
      >
        <PlaidEmbeddedLink
          token={embeddedToken}
          onSuccess={onSuccess}
          onExit={onExit}
          style={{width: "100%", height: EMBEDDED_MIN_HEIGHT}}
        />
      </Box>
      {hasMethods && (
        <Button buttonType="text" type="button" onClick={onCancelConnect}>
          Cancel
        </Button>
      )}
    </Container>
  );
};

export default PlaidBankSection;
